import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Spinner, Pagination, Breadcrumb } from "react-bootstrap";
import { _get } from "../services/apiClient";
import { apiUrls } from "../config/apiUrls.const";
import ServiceItemGrid from "./components/ServiceItemGrid";
import { BookingForm, BookingFormRef } from "./components/BookingForm";
import MapModal from "./components/MapModal";

export default function CategorizedPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingFormRef = useRef<BookingFormRef>(null);

  // States
  const [items, setItems] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapModalData, setMapModalData] = useState<any | null>(null);

  // Extract all potential filters from URL
  const filters = {
    division_id: searchParams.get("division_id"),
    district_id: searchParams.get("district_id"),
    thana_id: searchParams.get("thana_id"),
    service_group: searchParams.get("service_group"),
    category_id: searchParams.get("category_id"),
    subcategory_id: searchParams.get("subcategory_id"),
    page: searchParams.get("page") || "1",
  };

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await _get(apiUrls.serviceItemsSearch, filters);
      const payload = response.data || {};
      setItems(payload.data || []);
      setMeta(payload.meta || null);
    } catch (err) {
      console.error("API Error:", err);
      setError("Could not load services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [searchParams]); // Re-run whenever the URL changes

  const handlePageChange = (pageNumber: number) => {
    const params = Object.fromEntries(searchParams);
    setSearchParams({ ...params, page: pageNumber.toString() });
    window.scrollTo(0, 0); // Scroll to top on page change
  };

  return (
    <Container className="py-4">
      {/* 1. Breadcrumbs for easy navigation */}
      <Breadcrumb className="custom-breadcrumb">
        <Breadcrumb.Item onClick={() => navigate("/")}>Home</Breadcrumb.Item>
        {filters.service_group && (
          <Breadcrumb.Item active className="text-capitalize">
            {filters.service_group}
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      {/* 2. Dynamic Title */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-header mb-0">
          {filters.service_group ? filters.service_group.toUpperCase() : "Available"} Services
          <small className="text-muted fs-6 ms-2">
            ({meta?.total || 0} items found)
          </small>
        </h2>
      </div>
      
      <hr className="section-devider" />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* 3. Content Area */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <ServiceItemGrid
            items={items}
            title={filters.service_group || "Services"}
            handleItemClick={(id) => navigate(`/service-details?id=${id}`)}
            businessPage={(code) => navigate(`/webpage/${code}`)}
            handleMapModal={(lat, lon, addr) => setMapModalData({ lat, lon, addr })}
            bookingFormRef={bookingFormRef}
          />

          {/* 4. Pagination */}
          {meta && meta.last_page > 1 && (
            <Pagination className="justify-content-center mt-5 shadow-sm">
              <Pagination.Prev 
                disabled={meta.current_page === 1} 
                onClick={() => handlePageChange(meta.current_page - 1)} 
              />
              {[...Array(meta.last_page)].map((_, i) => (
                <Pagination.Item 
                  key={i + 1} 
                  active={i + 1 === meta.current_page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next 
                disabled={meta.current_page === meta.last_page} 
                onClick={() => handlePageChange(meta.current_page + 1)} 
              />
            </Pagination>
          )}
        </>
      )}

      {/* Common Modals */}
      {mapModalData && (
        <MapModal 
          show={!!mapModalData} 
          onHide={() => setMapModalData(null)} 
          lat={mapModalData.lat} 
          lon={mapModalData.lon} 
          address={mapModalData.addr} 
        />
      )}
      <BookingForm ref={bookingFormRef} onSuccess={() => {}} />
    </Container>
  );
}
